/**
 * Marco de la aplicacion: cabecera, navegacion, organizacion activa, estado de
 * la API y del tiempo real, y avisos flotantes.
 */
import { useEffect, useState } from 'react';
import type { ReactElement, ReactNode } from 'react';
import type { HealthResponse } from '@shared/api-types.ts';
import { MEMBER_ROLE_LABELS } from '@domain/types.ts';
import { api, isAbortError } from '../api.ts';
import type { Notice } from '../hooks.ts';
import { useRealtimeStatus } from '../hooks.ts';
import type { Route, Session } from '../state.ts';
import { SSE_STATUS_LABELS } from '../sse.ts';
import { Avatar } from './Avatar.tsx';
import { NoticeStack } from './Feedback.tsx';
import { Icon } from './Icon.tsx';
import type { IconName } from './Icon.tsx';

type NavItem = {
  route: Route;
  label: string;
  title: string;
  icon: IconName;
  /** Solo visible para el personal de la plataforma (`user.isSuperAdmin`). */
  superAdminOnly?: boolean;
};

const NAV_ITEMS: NavItem[] = [
  { route: { name: 'inbox', conversationId: null }, label: 'Bandeja', title: 'Bandeja compartida', icon: 'inbox' },
  { route: { name: 'metrics' }, label: 'Metricas', title: 'Metricas de atencion', icon: 'metrics' },
  { route: { name: 'ai' }, label: 'IA', title: 'Configuracion del agente de IA', icon: 'ai' },
  { route: { name: 'knowledge' }, label: 'Conocimiento', title: 'Base de conocimiento', icon: 'knowledge' },
  { route: { name: 'team' }, label: 'Equipo', title: 'Equipo de agentes', icon: 'team' },
  {
    route: { name: 'contacts' },
    label: 'Contactos',
    title: 'Contactos de la organizacion',
    icon: 'contacts',
  },
  { route: { name: 'accounts' }, label: 'Cuentas', title: 'Cuentas de WhatsApp', icon: 'accounts' },
  {
    route: { name: 'templates' },
    label: 'Plantillas',
    title: 'Plantillas de mensaje aprobadas en Meta',
    icon: 'templates',
  },
  { route: { name: 'apiKeys' }, label: 'Claves API', title: 'Claves de API para integraciones externas', icon: 'key' },
  {
    route: { name: 'tenants' },
    label: 'Organizaciones',
    title: 'Clientes de la plataforma (solo personal interno)',
    icon: 'tenants',
    superAdminOnly: true,
  },
];

type HealthState =
  | { kind: 'loading' }
  | { kind: 'ok'; health: HealthResponse }
  | { kind: 'error' };

function HealthBadge(): ReactElement {
  const [state, setState] = useState<HealthState>({ kind: 'loading' });

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    const load = async (): Promise<void> => {
      try {
        const health = await api.health(controller.signal);
        if (!cancelled) setState({ kind: 'ok', health });
      } catch (error) {
        if (!cancelled && !isAbortError(error)) setState({ kind: 'error' });
      }
    };

    void load();
    const timer = window.setInterval(() => void load(), 30_000);
    return () => {
      cancelled = true;
      controller.abort();
      window.clearInterval(timer);
    };
  }, []);

  if (state.kind === 'loading') {
    return (
      <span className="health" title="Comprobando el estado de la API">
        <span className="health__dot health__dot--muted" />
        API
      </span>
    );
  }

  if (state.kind === 'error') {
    return (
      <span className="health" title="No se pudo consultar /health: la API no responde">
        <span className="health__dot health__dot--danger" />
        API caida
      </span>
    );
  }

  const { health } = state;
  const ok = health.status === 'ok' && health.database;
  const detail = [
    `Base de datos: ${health.database ? 'ok' : 'con problemas'}`,
    `Cola: ${health.queue ? 'ok' : 'con problemas'}`,
    `Version: ${health.version}`,
  ].join(' · ');

  return (
    <span className="health" title={detail}>
      <span className={ok ? 'health__dot health__dot--ok' : 'health__dot health__dot--warn'} />
      {ok ? 'API ok' : 'API degradada'}
    </span>
  );
}

function ConnectionPill(): ReactElement {
  const status = useRealtimeStatus();
  const tone = status === 'open' ? 'ok' : status === 'offline' ? 'danger' : 'warn';
  return (
    <span className="conn" title="Estado de la conexion de eventos en tiempo real">
      <span className={`conn__dot conn__dot--${tone}`} />
      {SSE_STATUS_LABELS[status]}
    </span>
  );
}

export function AppShell({
  session,
  route,
  onNavigate,
  onLogout,
  onSwitchTenant,
  notices,
  onDismissNotice,
  children,
}: {
  session: Session;
  route: Route;
  onNavigate: (route: Route) => void;
  onLogout: () => void;
  onSwitchTenant: (tenantId: string) => void;
  notices: Notice[];
  onDismissNotice: (id: number) => void;
  children: ReactNode;
}): ReactElement {
  const status = useRealtimeStatus();
  const membership =
    session.memberships.find((item) => item.tenantId === session.tenantId) ?? session.memberships[0];
  const roleLabel = membership ? MEMBER_ROLE_LABELS[membership.role] : 'Agente';
  // "Organizaciones" solo existe para el personal de la plataforma.
  const navItems = NAV_ITEMS.filter((item) => !item.superAdminOnly || session.user.isSuperAdmin);

  return (
    <div className="shell">
      <header className="topbar">
        <div className="topbar__brand">
          <span className="topbar__logo">WA</span>
          <div className="topbar__brandtext">
            <span className="topbar__title">Atencion al cliente</span>
            <span className="topbar__subtitle">{membership?.tenantName ?? 'Sin organizacion'}</span>
          </div>
        </div>

        <nav className="topbar__nav" aria-label="Secciones del panel">
          {navItems.map((item) => {
            const active = item.route.name === route.name;
            return (
              <button
                type="button"
                key={item.label}
                className={active ? 'navitem is-active' : 'navitem'}
                title={item.title}
                aria-current={active ? 'page' : undefined}
                onClick={() => onNavigate(item.route)}
              >
                <Icon name={item.icon} />
                <span className="navitem__label">{item.label}</span>
              </button>
            );
          })}
        </nav>

        <div className="topbar__right">
          <ConnectionPill />
          <HealthBadge />

          {session.memberships.length > 1 ? (
            <label className="tenant">
              <span className="tenant__label">Organizacion</span>
              <select
                className="select select--compact"
                value={session.tenantId}
                onChange={(event) => onSwitchTenant(event.target.value)}
              >
                {session.memberships.map((item) => (
                  <option key={item.tenantId} value={item.tenantId}>
                    {item.tenantName}
                  </option>
                ))}
              </select>
            </label>
          ) : null}

          <div className="userbox">
            <Avatar name={session.user.name} seed={session.user.id} size="sm" />
            <div className="userbox__text">
              <span className="userbox__name">{session.user.name}</span>
              <span className="userbox__role">{roleLabel}</span>
            </div>
          </div>

          <button type="button" className="button button--ghost button--sm" onClick={onLogout} title="Cerrar sesion">
            <Icon name="logout" />
            Salir
          </button>
        </div>
      </header>

      {status !== 'open' ? (
        <div className={status === 'offline' ? 'strip strip--danger' : 'strip strip--warning'} role="status">
          <Icon name={status === 'offline' ? 'error' : 'clock'} />
          <span>
            {status === 'connecting'
              ? 'Conectando con el servicio de eventos…'
              : status === 'reconnecting'
                ? 'Se ha perdido la conexion en tiempo real. Reintentando automaticamente…'
                : 'Sin conexion en tiempo real: los cambios no se actualizaran solos hasta que vuelva la conexion.'}
          </span>
        </div>
      ) : null}

      <main className="shell__body">{children}</main>

      <NoticeStack notices={notices} onDismiss={onDismissNotice} />
    </div>
  );
}

/** Mensaje comun cuando falta organizacion activa. */
export function TenantWarning(): ReactElement {
  return (
    <div className="banner banner--warning">
      <Icon name="warning" className="banner__icon" />
      <p className="banner__text">
        Tu usuario no pertenece a ninguna organizacion, asi que no hay datos que mostrar.
      </p>
    </div>
  );
}

/** Mensaje para quien abre una seccion reservada al personal de la plataforma. */
export function PlatformStaffWarning(): ReactElement {
  return (
    <div className="page">
      <div className="banner banner--warning" role="status">
        <Icon name="warning" className="banner__icon" />
        <p className="banner__text">
          Esta seccion es exclusiva del personal de la plataforma.
        </p>
      </div>
    </div>
  );
}
